import { SectionData } from '@/typings/interfaces/data/section';
import { AlignContentType, CookieConsentProvider } from '@/typings/enums/enums';
import { BaseIntegrationData } from '@/models/integrations/BaseIntegration';
import { DeviceData } from '@/hooks/useDevice';

export enum MessageTypes {
  CHEATING = 'cheating',
  EXPIRED = 'expired',
  EXPIRED_CAMPAIGN = 'expired_campaign',
  GAME_LIMIT = 'game_limit',
  PAUSED_CAMPAIGN = 'paused_campaign'
}

export enum MessageDisplayTypes {
  REDIRECT = 'redirect',
  FLOWPAGE = 'flowpage',
  INLINE_MESSAGE = 'inline_message'
}

export interface AddonEditItem {
  name: string;
  description: string;
  icon: string;
  category: string;
  area: string;
}

interface CustomFontData {
  id: number;
  css: string;
  fontFamily: string;
}

export interface CampaignConfigData {
  campaign_id: number;
  campaign_url: string;
  customer_id: number;
  title: string;
  name: string;
  sdk: number;
  lead_score_enabled: boolean;
  game_type: string;
  game_alias: string;
  game_icon: string;
  paused: number;
  time?: {
    unix: number;
    formatted: string;
  };
  asset_url: string;
  favicon?: string;
  echo: string;
  api_endpoint?: string;
  analytics?: {
    base_path: string;
  };
  edit: {
    enabled: boolean;
    csrf_token?: string;
    addons?: { [key: string]: AddonEditItem };
  };
  ipstack_api?: string;
  language: number;
  language_code: string;
  timezone: string;
  active_from?: string;
  active_to?: string;
  global_layout: boolean;
  gtm_id?: string;
  enable_only_allow_viewing_in_iframe: boolean;
  expired: boolean;
  upload_proxy?: string;
  ghost_user: boolean;
  deactivate_internal_link_tracking: boolean;
  utm_enabled: boolean;
  utm_campaign: string;
  utm_medium: string;
  custom_fonts: CustomFontData[];
  deactivate_ga?: number;
  platform: string;
  plan: string;
  has_registrations: boolean;
  hash: string;
  cookies_enabled?: boolean;
  cookie_consent?: {
    provider: CookieConsentProvider;
  };
  integrations: BaseIntegrationData[];
  resources: {
    before_body_close?: string;
    before_body_start?: string;
    css?: string;
    head?: string;
  };
  fb_app_id: string;
  fb_verification_id: string;
  meta_tags: MetaTag[];
  replacementtags?: {
    [key: string]: unknown;
  };
}

interface MetaTag {
  [key: string]: string;
}

export interface CampaignData {
  advanced: CampaignAdvancedData;
  config: CampaignConfigData;
  sections: SectionData[];
  global_layout?: DeviceData<CampaignLayoutData>;
  layout?: DeviceData<CampaignLayoutData>;
  pages?: {
    flow?: {
      after: [];
      all: SectionData[];
      before: [];
    };
    pages: SectionData[];
  };
  translation?: CampaignTranslationData<{ [key: string]: { [key: string]: string } }>;
}
interface CampaignCSSData {
  css?: string;
}

interface ExternalCSSData {
  name?: string;
  url?: string;
}

interface JavascriptData {
  javascript: string;
}

export interface CampaignAdvancedData {
  settings: CampaignSettingsData;
  css: CampaignCSSData;
  externalcss: ExternalCSSData;
  javascript: JavascriptData;
  [key: string]: CampaignSettingsData | CampaignMessageData | CampaignCSSData | ExternalCSSData | JavascriptData;
}

interface CampaignSettingsData {
  preloader?: CampaignPreloaderData;
  favicon?: string;
  retention?: CampaignRetentionData;
  control_code: CampaignControlCodeData;
}

export interface CampaignMessageData {
  description?: string;
  headline: string;
  message: MessageTypes;
  redirect_target?: '_blank' | '_self' | '_top';
  display_type?: MessageDisplayTypes;
  redirect_url?: string;
  flow_page?: string;
  inline_styles?: string;
}

interface CampaignPreloaderData {
  type: string;
  color: string;
  effect?: string;
  image?: string;
  keep_size?: string;
  overlay: OverlayData;
}

interface OverlayData {
  enabled: string;
  color: string;
}

interface CampaignRetentionData {
  timing: string;
}

interface CampaignControlCodeData {
  enabled: string;
  label: string;
  type: 'bottom' | 'top' | 'overlay' | 'replacementtag';
  background_color: string;
  text_color: string;
}

export interface CampaignLayoutData {
  base_color?: string;
  background_color?: string;
  text_color?: string;
  button_text_color?: string;
  background_image?: string;
  background_type?: string;
  background_attachment_fixed?: string;
  background_position: string;
  font_family?: string;
  button_style?: string;
  flow?: CampaignLayoutFlowData;
  custom_popover_colors?: string;
  popover_background_color?: string;
  popover_text_color?: string;
  overwrite?: string;
  scroll_indicator?: CampaignLayoutScrollIndicatorData;
  styles?: StylesData;
  grid?: CampaignGridData;
  buttons?: CampaignLayoutButtonsData;
}

export interface StylesData {
  [key: string]: StyleElementData;
}

export interface StyleElementData {
  element: string;
  type: string;
  size: string;
  color?: string;
  line_height: string;
  weight: string;
  style?: string[];
}

interface CampaignLayoutFlowData {
  transition?: string;
  overwrite?: string;
  overlay?: string;
}

interface CampaignLayoutScrollIndicatorData {
  enable?: string;
  color?: string;
}

export interface CampaignLayoutButtonsData {
  add_shadow: string;
  background_color: string;
  text_color: string;
  vertical_padding: string;
  horisontal_padding: string;
  font_family: string;
  text_size: string;
  text_weight: string;
  letter_spacing?: string;
  border_color?: string;
  border_thickness?: string;
  border_radius?: string;
  line_height?: string;
  button_width?: string;
  background_color_hover?: string;
  text_color_hover?: string;
  border_color_hover?: string;
  button_effect?: string;
  overwrite?: string;
  text_style?: string[];
  hover_text_style?: string[];
  shadow_blur: string;
  shadow_color: string;
  shadow_horizontal: string;
  shadow_spread: string;
  shadow_vertical: string;
  secondary?: ButtonSecondaryData;
}

export interface CampaignGridData {
  desktop?: GridData;
  tablet?: GridData;
}

interface GridData {
  pixel_size?: string;
  align?: AlignContentType;
  type_pixel?: string;
  type_percent?: string;
  percent_size?: string;
}

interface ButtonSecondaryData {
  add_shadow: string;
  background_color?: string;
  text_color?: string;
  vertical_padding?: string;
  horisontal_padding?: string;
  font_family?: string;
  text_size?: string;
  text_weight?: string;
  letter_spacing?: string;
  border_color?: string;
  border_thickness?: string;
  border_radius?: string;
  line_height?: string;
  button_width?: string;
  background_color_hover?: string;
  text_color_hover?: string;
  text_style?: string[];
  hover_text_style?: string[];
  border_color_hover?: string;
  button_effect?: string;
  shadow_blur?: string;
  shadow_color?: string;
  shadow_horizontal?: string;
  shadow_spread?: string;
  shadow_vertical?: string;
  overwrite?: string;
}

export interface CampaignTranslationData<Translation> {
  addons: Translation;
  days: CampaignTranslationTime;
  form: Translation;
  games: Translation;
  hours: CampaignTranslationTime;
  minutes: CampaignTranslationTime;
  seconds: CampaignTranslationTime;
}
interface CampaignTranslationTime {
  plural: string;
  shortened: string;
  single: string;
  isPopup: boolean;
}
